import React, { useEffect, useRef, useState } from 'react';

/**
 * DispatchScrollSync
 *
 * Adds a horizontal scrollbar ABOVE the Dispatch board that stays in sync
 * with the main scroll area below it. Purely a usability affordance — the
 * main board keeps its own native scrollbar at the bottom.
 *
 * Approach:
 *  - Render a 14-20px-tall scroll proxy div whose inner spacer width tracks
 *    `contentRef.current.scrollWidth`.
 *  - Two-way sync via `scroll` events + `isSyncingRef` guard to prevent loops.
 *  - ResizeObserver + MutationObserver keep the proxy width correct when the
 *    board width changes (columns added / removed, window resize).
 *  - Hidden under `md` breakpoint — mobile/tablet already scroll with touch,
 *    and the extra bar just steals vertical space.
 *  - Hidden when the board isn't scrollable (scrollWidth <= clientWidth).
 */
const DispatchScrollSync = ({ contentRef }) => {
  const proxyRef = useRef(null);
  const spacerRef = useRef(null);
  const isSyncingRef = useRef(false);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    const content = contentRef?.current;
    const proxy = proxyRef.current;
    const spacer = spacerRef.current;
    if (!content || !proxy || !spacer) return undefined;

    const measure = () => {
      const scrollable = content.scrollWidth > content.clientWidth + 1;
      setIsScrollable(scrollable);
      spacer.style.width = `${content.scrollWidth}px`;
    };

    const onContentScroll = () => {
      if (isSyncingRef.current) { isSyncingRef.current = false; return; }
      isSyncingRef.current = true;
      proxy.scrollLeft = content.scrollLeft;
    };

    const onProxyScroll = () => {
      if (isSyncingRef.current) { isSyncingRef.current = false; return; }
      isSyncingRef.current = true;
      content.scrollLeft = proxy.scrollLeft;
    };

    measure();
    content.addEventListener('scroll', onContentScroll, { passive: true });
    proxy.addEventListener('scroll', onProxyScroll, { passive: true });

    const ro = new ResizeObserver(measure);
    ro.observe(content);
    ro.observe(content.firstElementChild || content);

    const mo = new MutationObserver(measure);
    mo.observe(content, { childList: true, subtree: true });

    window.addEventListener('resize', measure);

    return () => {
      content.removeEventListener('scroll', onContentScroll);
      proxy.removeEventListener('scroll', onProxyScroll);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [contentRef]);

  return (
    <div
      ref={proxyRef}
      // Hidden on mobile (<md); only rendered when board actually overflows.
      className={`hidden md:block overflow-x-auto overflow-y-hidden h-4 mx-4 mt-1 rounded ${isScrollable ? 'visible' : 'invisible h-0 mt-0'}`}
      data-testid="dispatch-top-scroll-proxy"
      aria-hidden="true"
    >
      <div ref={spacerRef} style={{ height: 1, minWidth: '100%' }} />
    </div>
  );
};

export default DispatchScrollSync;
